import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import "./finance.css";
import FinanceList from "../financeList/financeList";
import FinanceTransfers from "../financeTransfers/financeTransfers";

export const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");

export const STATUS_LABEL = {
    paid: "Оплачено",
    partial: "Оплачено частично",
    unpaid: "Не оплачено",
    transfer: "Перечисление",
    cash: "Наличка",
};

export const METHOD_LABEL = { cash: "Наличные", transfer: "Перечисление", terminal: "Клик / Терминал" };

const Finance = ({ logout, user, API_URL, restrictedToTransfers }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const activeTab = location.pathname.startsWith("/finance/transfers")
        ? "transfers"
        : "debtors";

    return (
        <div className="finance">
            <div className="fn-tabs">
                {!restrictedToTransfers && (
                    <div className={`fn-tab${activeTab === "debtors" ? " active" : ""}`} onClick={() => navigate("/finance")}>
                        Заказы
                    </div>
                )}
                <div className={`fn-tab${activeTab === "transfers" ? " active" : ""}`} onClick={() => navigate("/finance/transfers")}>
                    Счет-фактуры
                </div>
              
            </div>

            <Routes>
                <Route path="/transfers" element={<FinanceTransfers API_URL={API_URL} />} />
                {!restrictedToTransfers && (
                    <>
                        <Route path="/" element={<DebtorsList API_URL={API_URL} />} />
                        <Route path="/company/:company" element={<FinanceListRoute API_URL={API_URL} />} />
                    </>
                )}
                <Route path="*" element={<Navigate to="/finance/transfers" />} />
            </Routes>
        </div>
    );
};

// Обёртка: берёт company из URL и прокидывает в FinanceList как раньше
function FinanceListRoute({ API_URL }) {
    const { company } = useParams();
    const navigate = useNavigate();
    return (
        <FinanceList
            API_URL={API_URL}
            company={decodeURIComponent(company)}
            onBack={() => navigate("/finance")}
            onOpenOrder={(o) => navigate(`/financedetails/${o.requestType}/${o.orderNumber}`)}
        />
    );
}

// ─────────────────────────────────────────────────────────────
//  page1 — список должников
// ─────────────────────────────────────────────────────────────
function DebtorsList({ API_URL }) {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [soloOrders, setSoloOrders] = useState([]);
    const [stats, setStats] = useState({ companiesCount: 0, totalDebt: 0, paidThisMonth: 0 });
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getFinanceDebtors`);
            if (data.status === "success") {
                setCompanies(data.companies);
                setSoloOrders(data.soloOrders);
                setStats(data.stats);
            }
        } catch (err) {
            console.error("getFinanceDebtors:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => { load(); }, [load]);

    const q = search.trim().toLowerCase();

    const filteredCompanies = useMemo(() => companies
        .filter((c) => c.company.toLowerCase().includes(q))
        .filter((c) => {
            if (filter === "debt") return c.debtSum > 0;
            if (filter === "paid") return c.allPaid;
            if (filter === "partial") return c.hasPartial;
            if (filter === "unpaid") return c.hasUnpaid;
            if (filter === "transfer") return c.hasTransfer;
            if (filter === "cash") return c.hasCash;
            return true;
        }), [companies, q, filter]);

    const filteredSolo = useMemo(() => soloOrders
        .filter((o) => o.title.toLowerCase().includes(q) || String(o.orderNumber).includes(q))
        .filter((o) => {
            if (filter === "debt") return o.isOverdue;
            if (filter === "paid") return o.status === "paid" && !o.isOverdue;
            if (filter === "partial") return o.status === "partial";
            if (filter === "unpaid") return o.status === "unpaid";
            if (filter === "transfer") return o.paymentType === "transfer";
            if (filter === "cash") return o.paymentType === "cash";
            return true;
        }), [soloOrders, q, filter]);

    const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

    return (
        <>
            <div className="fn-topbar">
                <div className="fn-search">⌕ <input placeholder="Поиск по компании / заказу" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
                <div className="fn-pagetitle">Финансы</div>
            </div>

            <div className="fn-stats">
                <div className="fn-stat"><div className="fn-lbl">Компаний с заказами</div><div className="fn-val">{stats.companiesCount}</div></div>
                <div className="fn-stat debt"><div className="fn-lbl">Общий остаток долга</div><div className="fn-val">${fmt(stats.totalDebt)}</div></div>
            </div>

            <div className="fn-toolbar">
                <span className={`fn-chip${filter === "all" ? " active" : ""}`} onClick={() => setFilter("all")}>Все</span>
                <span className={`fn-chip${filter === "transfer" ? " active" : ""}`} onClick={() => setFilter("transfer")}>
                    <span className="fn-dot" style={{ background: "var(--fn-blue)" }} />Перечисление
                </span>
                <span className={`fn-chip${filter === "cash" ? " active" : ""}`} onClick={() => setFilter("cash")}>
                    <span className="fn-dot" style={{ background: "#2563EB" }} />Наличка
                </span>
                <span className={`fn-chip${filter === "debt" ? " active" : ""}`} onClick={() => setFilter("debt")}>
                    <span className="fn-dot" style={{ background: "var(--fn-red)" }} />Долг
                </span>
                <span className={`fn-chip${filter === "paid" ? " active" : ""}`} onClick={() => setFilter("paid")}>
                    <span className="fn-dot" style={{ background: "var(--fn-green)" }} />Оплачено
                </span>
                <span className={`fn-chip${filter === "partial" ? " active" : ""}`} onClick={() => setFilter("partial")}>
                    <span className="fn-dot" style={{ background: "var(--fn-amber)" }} />Оплачено частично
                </span>
                <span className={`fn-chip${filter === "unpaid" ? " active" : ""}`} onClick={() => setFilter("unpaid")}>
                    <span className="fn-dot" style={{ background: "#8890A0" }} />Не оплачено
                </span>
            </div>

            {loading ? <div className="fn-empty">Загрузка…</div> : (
                <>
                    <div className="fn-section-label">Компании-партнёры</div>
                    {filteredCompanies.length === 0 ? <div className="fn-empty">Компаний не найдено</div> : (
                        filteredCompanies.map((c) => (
                            <div className="fn-debtor" key={c.company} style={{ cursor: "pointer" }} onClick={() => navigate(`/finance/company/${encodeURIComponent(c.company)}`)}>
                                <div className="fn-badge">{initials(c.company)}</div>
                                <div className="fn-info">
                                    <div className="fn-name">{c.company}</div>
                                    <div className="fn-sub">
                                        {c.ordersCount} заказ(ов)
                                        {c.lastIncome ? ` · последний приход ${new Date(c.lastIncome).toLocaleDateString("ru-RU")}` : ""}
                                    </div>
                                </div>
                                <div className="fn-metrics">
                                    <div className="fn-metric"><div className="fn-m-lbl">Сумма</div><div className="fn-m-val">${fmt(c.totalSum)}</div></div>
                                    <div className="fn-metric">
                                        <div className="fn-m-lbl">Оплачено</div>
                                        <div className="fn-m-val" style={{ color: "var(--fn-green)" }}>${fmt(c.paidSum)}</div>
                                    </div>
                                    <div className={`fn-metric${c.debtSum > 0 ? " debt" : ""}`}>
                                        <div className="fn-m-lbl">Долг</div>
                                        <div className="fn-m-val" style={c.debtSum === 0 ? { color: "var(--fn-green)" } : undefined}>${fmt(c.debtSum)}</div>
                                    </div>
                                </div>
                                <button className="fn-go" style={{ cursor: "pointer" }}>Открыть →</button>
                            </div>
                        ))
                    )}

                    <div className="fn-section-label">Индивидуальные и групповые заказы</div>
                    {filteredSolo.length === 0 ? <div className="fn-empty">Заказов не найдено</div> : (
                        filteredSolo.map((o) => {
                            const paidAmount = o.paidAmount == null ? 0 : o.paidAmount;
                            return (
                                <div className={`fn-order${o.isOverdue ? " fn-order--overdue" : ""}`} key={`${o.requestType}-${o.orderNumber}`} onClick={() => navigate(`/financedetails/${o.requestType}/${o.orderNumber}`)}>
                                    <div className="fn-badge fn-badge--order">{o.requestType === "sharetour" ? "ST" : initials(o.title)}</div>
                                    <div className="fn-info">
                                        <div className="fn-name">{o.title}</div>
                                        <div className="fn-sub">
                                            № {o.orderNumber} · {o.requestType === "sharetour" ? "Share Tour" : "Индивидуальный"}
                                            {o.date ? ` · ${new Date(o.date).toLocaleDateString("ru-RU")}` : ""}
                                        </div>
                                    </div>
                                    <div className="fn-metrics">
                                        <div className="fn-metric"><div className="fn-m-lbl">Сумма</div><div className="fn-m-val">${fmt(o.grandTotal)}</div></div>
                                        <div className="fn-metric">
                                            <div className="fn-m-lbl">Оплачено</div>
                                            <div className="fn-m-val" style={{ color: "var(--fn-green)" }}>${fmt(paidAmount)}</div>
                                        </div>
                                        {o.debtAmount > 0 && (
                                            <div className={`fn-metric${o.isOverdue ? " debt" : ""}`}>
                                                <div className="fn-m-lbl">Остаток</div>
                                                <div className="fn-m-val">${fmt(o.debtAmount)}</div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="fn-pill-stack">
                                        <span className={`fn-pill ${o.status}`}>{STATUS_LABEL[o.status]}</span>
                                        {o.isOverdue && <span className="fn-pill debt">Долг</span>}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </>
            )}
        </>
    );
}

export default Finance;